module Meevo.Plugins.QuickbooksDesktop {

    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;

    export interface IQuickbooksDesktopApi {
        getExternalAccounts: (applicationConnectionId: Guid) => ng.IPromise<IExternalAccount[]>;
        getConnectionsCount: (criteria: IAccountCountRequest) => ng.IPromise<number>;
        getSystemAccounts: () => ng.IPromise<IAccountSystemList[]>;
        getConnections: () => ng.IPromise<any>;
        getConnectionById: (applicationConnectionId: Guid) => ng.IPromise<any>;
        setupConnection: (connection: IConnectionSetupInfo) => ng.IPromise<IApplicationConnection>;
        updateConnection: (connectionInfo: IConnectionSetupInfo) => ng.IPromise<IConnectionSetupInfo>;
        deleteConnection: (criteria: any) => ng.IPromise<IApplicationConnection>;
        getPaymentTypeList: (requestParams: IPaymentTypeCriteria) => ng.IPromise<Meevo.Model.PaymentTypeListItem[]>;
        addAccountsToConnection: (connection: IConnectionSetupInfo & IAccountsSetup) => ng.IPromise<IAccountsRetentionStatus>;
        syncData: (syncData: ISyncRequestData) => ng.IPromise<any>;
        getLastSyncData: (applicationConnectionId: Guid) => ng.IPromise<any>;
        getNotClosedDrawersList: (criteria: any) => ng.IPromise<any>;
        deleteSync: (syncForRemoved: ISyncRequestData) => ng.IPromise<string>;
        deleteQueue: (queueEntries: { items: Array<IQueueDelete> }) => ng.IPromise<string>;

    }

    export class QuickbooksDesktopApi implements IQuickbooksDesktopApi {
        static $inject = [
            'meevo.components.common.IdentityService',
            'meevo.components.common.DataService',
            'meevo.AppSettings'
        ];

        constructor(
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected appSettings: Meevo.Components.Common.IAppSettings
        ) { }

        /**
         * Get list of connections
         * @returns {ng.IPromise<Array<IConnectionListItem>>} promise
         */
        getConnections(): ng.IPromise<Array<IConnectionListItem>> {
            const body = {
                criteria: {
                    ObjectState: ObjectStateEnum.Active,
                    ApplicationConnectionType: 1
                }
            };
            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.accountsyncDesktop, body);
        }

        /**
         * Get count of connections
         * @param {IAccountCountRequest} criteria
         * @returns {ng.IPromise<number>} promise
         */
        getConnectionsCount(criteria: IAccountCountRequest): ng.IPromise<number> {
            return this.dataService.getCount(this.appSettings.API_ENDPOINTS.accountsyncDesktop, criteria);
        }

        /**
         * Get connection using id
         * @param {Guid} applicationConnectionId
         * @returns {ng.IPromise<any>} promise
         */
        // Todo: added type of return statment
        getConnectionById(applicationConnectionId: Guid): ng.IPromise<any> {
            return this.dataService.getItem(this.appSettings.API_ENDPOINTS.accountsyncDesktop, applicationConnectionId);
        }

        /**
         * Setup new connection
         * @param {IConnectionSetupInfo} connection
         * @returns {ng.IPromise<any>} promise
         */
        setupConnection(connection: IConnectionSetupInfo): ng.IPromise<IApplicationConnection> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/ConnectionSetup`, connection);
        }

        /**
         * Update connection info
         * @param {IConnectionSetupInfo} connectionInfo
         * @returns {ng.IPromise<IConnectionSetupInfo>} promise
         */
        updateConnection(connectionInfo: IConnectionSetupInfo): ng.IPromise<IConnectionSetupInfo> {
            return this.dataService.update(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/ConnectionSetup`, connectionInfo);
        }

        /**
         * Delete connection using id
         * @param {any} criteria
         * @returns {ng.IPromise<IApplicationConnection>} promise
         */
        deleteConnection(criteria: any): ng.IPromise<IApplicationConnection> {
            return this.dataService.patch(this.appSettings.API_ENDPOINTS.accountsyncDesktop, criteria);
        }
         
        /**
         * Get system accounts
         * @returns {ng.IPromise<IAccountSystemList[]>} promise
         */
        getSystemAccounts(): ng.IPromise<IAccountSystemList[]> {
            return this.dataService.getItem(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/AccountSystem/List`);
        }

        /**
         * Get external accounts
         * @param {Guid} applicationConnectionId
         * @returns {ng.IPromise<IExternalAccount[]>} promise
         */
        getExternalAccounts(applicationConnectionId: Guid): ng.IPromise<IExternalAccount[]> {
            const body = {
                applicationConnectionId,
                tenantId: this.identityService.current().tenantId,
                locationId: this.identityService.current().locationId
            }
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/Accounts/List`, body);
        }

        /**
         * Get list of Payment Type
         * @param {IPaymentTypeCriteria} accountTypeId
         * @returns {ng.IPromise<Meevo.Model.PaymentTypeListItem[]>} promise
         */
        getPaymentTypeList(requestParams: IPaymentTypeCriteria): ng.IPromise<Meevo.Model.PaymentTypeListItem[]> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.paymentType}/List/`, 'POST', requestParams)
                .then(response => response.data);
        }     

        /**
         * Attached accounts to connection
         * @param {IConnectionSetupInfo & IAccountsSetup} accountsInfo
         * @returns {ng.IPromise<IAccountsRetentionStatus>} promise
         */
        addAccountsToConnection(connection: IConnectionSetupInfo & IAccountsSetup): ng.IPromise<IAccountsRetentionStatus> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/AccountSetup`, connection);
        }

        // Sync Tab

        /**
         * Sync data for chosen connection
         * @param {ISyncRequestData} syncData
         * @returns {ng.IPromise<string>} promise
         */
        syncData(syncData: ISyncRequestData): ng.IPromise<any> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/Sync`, syncData);
        }

        /**
         * Delete sync from the server for chosen connection
         * @param {ISyncRequestData} syncForRemoved
         * @returns {ng.IPromise<string>} promise
         */
        deleteSync(syncForRemoved: ISyncRequestData): ng.IPromise<string> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/Delete`, 'POST', syncForRemoved)
                .then(response => response.data);
        }

        /**
         * Delete queue entries
         * @param {IQueueDelete} queueEntries
         * @returns {ng.IPromise<string>} promise
         */
        deleteQueue(queueEntries: { items: Array<IQueueDelete> }): ng.IPromise<string> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/Queue/Delete`, 'POST', queueEntries)
                .then(response => response.data);
        }

        /**
         * Get the information about the last sync for chosen connection
         * @param {Guid} applicationConnectionId
         * @returns {ng.IPromise<ISyncOperation>} promise
         */
        getLastSyncData(applicationConnectionId: Guid): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.accountsyncDesktop}/LastSync`, 'POST', { applicationConnectionId })
                .then(response => response.data);
        }

        /**
         * Get not closed drawers list
         * @param {any} criteria
         * @returns {ng.IPromise<any>} promise
         */
        getNotClosedDrawersList(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.drawerSession}/List`, 'POST', criteria)
                .then(response => {
                    return response.data.filter(drawer => !drawer.closedDate);
                });
        }


    }

    angular.module('meevo.plugins.quickbooksDesktop')
        .service('meevo.plugins.quickbooksDesktop.QuickbooksDesktopApi', QuickbooksDesktopApi);
}
